import { motion } from "framer-motion";
import { GraduationCap, Phone, Mail } from "lucide-react";
import { useInView } from "@/hooks";
import { getOptimizedImageUrl } from "@/lib/cloudinary";
import { teamMemberService } from "@/services/firebase";
import { useState, useEffect } from "react";
import type { TeamMember } from "@/types";

export function TeamSection() {
  const { ref, isInView } = useInView();
  const [members, setMembers] = useState<TeamMember[]>([]);

  useEffect(() => {
    teamMemberService.getAll().then(setMembers);
  }, []);

  return (
    <section id="team" ref={ref} className="py-20 bg-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-dark mb-4">
            Trois experts, Une vision commune.
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            Allo Artisan, c'est l'histoire d'une équipe complémentaire, déterminée à prouver que l'innovation peut venir de chez nous, pour chez nous.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {members.map((member, index) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.15, duration: 0.5 }}
              className="bg-white rounded-2xl p-8 shadow-card text-center"
            >
              {/* Avatar placeholder */}
                {member.photoURL ? (
                  <img
                    src={getOptimizedImageUrl(member.photoURL, ["w_240", "h_240", "c_fill", "g_face"])}
                    alt={member.name}
                    className="w-24 h-24 rounded-full mx-auto mb-6 object-cover"
                  />
                ) : (
                  <div className="w-24 h-24 bg-gradient-to-br from-primary to-secondary rounded-full mx-auto mb-6 flex items-center justify-center text-white text-2xl font-bold">
                    {member.name.split(" ").map((n) => n[0]).join("")}
                  </div>
                )}
              
              <h3 className="text-xl font-bold text-dark mb-1">{member.name}</h3>
              <p className="text-primary font-medium text-sm mb-4">{member.role}</p>
              
              <p className="text-gray-500 text-sm leading-relaxed mb-6">{member.bio}</p>
              
              {member.skills && (
                <div className="flex flex-wrap justify-center gap-2 mb-4">
                  {member.skills.map((skill) => (
                    <span key={skill} className="px-3 py-1 bg-light rounded-full text-xs text-gray-600">
                      {skill}
                    </span>
                  ))}
                </div>
              )}
              
              <div className="flex justify-center gap-3 pt-4 border-t border-gray-100">
                {member.phone && (
                  <a href={`tel:${member.phone}`} className="w-9 h-9 bg-light rounded-lg flex items-center justify-center hover:bg-primary hover:text-white transition-colors text-gray-500">
                    <Phone className="w-4 h-4" />
                  </a>
                )}
                {member.email && (
                  <a href={`mailto:${member.email}`} className="w-9 h-9 bg-light rounded-lg flex items-center justify-center hover:bg-primary hover:text-white transition-colors text-gray-500">
                    <Mail className="w-4 h-4" />
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* INPHB Section */}
        {/* <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
          className="mt-16 bg-dark rounded-2xl p-8 md:p-10 text-center"
        >
          <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-6">
            <GraduationCap className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-xl font-bold text-white mb-3">INPHB — Institut National Polytechnique Félix Houphouët-Boigny</h3>
          <p className="text-white/60 max-w-2xl mx-auto text-sm leading-relaxed">
            Allo Artisan est né au sein de l'INPHB, établissement d'enseignement supérieur de référence en Côte d'Ivoire, situé à Yamoussoukro. 
            Les fondateurs y ont acquis leurs compétences techniques et managériales, développé leur sens de l'innovation, 
            et forgé leur conviction que les solutions aux défis africains doivent venir des Africains eux-mêmes.
          </p>
        </motion.div> */}
      </div>
    </section>
  );
}
